import React, { useCallback, useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import {
  Banner,
  Button,
  Checkbox,
  FieldBox,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { H1 } from '~/src/ui-kit';
import { ContentBox } from '~/src/components/ContentBox';
import { Lang, useLang } from '~/src/polyglot';

import { FormStyled, InputFieldsBox } from './styles';
import type { CreatePasswordProps } from './CreatePassword.types';
import { getErrorMessage } from './CreatePassword.helpers';

export function CreatePassword(props: CreatePasswordProps): ReactElement {
  const { errorMessage, createPasswordHandler } = props;
  const lang = useLang();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    trigger,
    watch,
    setValue,
    getValues,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      confirmPassword: '',
      allowMarketing: false,
    },
  });

  useEffect(() => {
    const subscription = watch(() => isSubmitted && trigger());

    return () => subscription.unsubscribe();
  }, [watch, isSubmitted, trigger]);

  const validatePasswords = useCallback((): boolean => {
    const { password, confirmPassword } = getValues();

    return !!password && password === confirmPassword;
  }, [getValues]);

  const isAllowMarketing = watch('allowMarketing');

  return (
    <ContentBox>
      <FormStyled onSubmit={handleSubmit(createPasswordHandler)}>
        <H1><Lang message="CREATE_PASSWORD.TITLE" /></H1>
        <Banner
          type="info"
          view="ghost"
          title={lang({ message: 'CREATE_PASSWORD.BANNER_TITLE' })}
        >
          <Text $variant="footnote">
            <Lang message="CREATE_PASSWORD.PASSWORD_HINT" />
          </Text>
        </Banner>
        <InputFieldsBox>
          <FieldBox
            title={lang({ message: 'CREATE_PASSWORD.FIELD.PASSWORD' })}
            error={!!errors.password && (
              <Text $variant="footnote" $color={Color.DANGER}>
                {getErrorMessage(errors.password, lang)}
              </Text>
            )}
          >
            <NativeInput
              autoFocus
              type="password"
              autoComplete="off"
              $isErrored={!!errors.password}
              {...register('password', { required: true, minLength: 8, validate: validatePasswords })}
            />
          </FieldBox>
          <FieldBox
            title={lang({ message: 'CREATE_PASSWORD.FIELD.REPEAT_PASSWORD' })}
            error={!!errors.confirmPassword && (
              <Text $variant="footnote" $color={Color.DANGER}>
                {getErrorMessage(errors.confirmPassword, lang)}
              </Text>
            )}
          >
            <NativeInput
              type="password"
              autoComplete="off"
              $isErrored={!!errors.confirmPassword}
              {...register('confirmPassword', { required: true, minLength: 8, validate: validatePasswords })}
            />
          </FieldBox>
          {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
        </InputFieldsBox>
        <Horizontal $alignItems="center">
          <Button
            type="submit"
            color="primary"
            isLoading={isSubmitting}
          >
            <Lang message="CREATE_PASSWORD.BUTTON.COMPLETE" />
          </Button>
        </Horizontal>
        <Checkbox
          label={lang({ message: 'CREATE_PASSWORD.CHECKBOX.MARKETING' })}
          name="allowMarketing"
          isChecked={watch('allowMarketing')}
          onChange={(): void => setValue('allowMarketing', !isAllowMarketing)}
        />
      </FormStyled>
    </ContentBox>
  );
}
